//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { SiAnthropic, SiGooglegemini, SiOllama } from 'react-icons/si';
import { VscOpenai, VscAzure } from 'react-icons/vsc';
import { Sparkles } from 'lucide-react';
import type { Provider } from './integrationSchema';

type ProviderIconComponent = React.ComponentType<{ className?: string }>;

// react-icons' IconType is `(props) => ReactNode`, which React 18's
// FunctionComponent typing (ReactElement | null return) rejects — cast once
// here per icon rather than fighting that mismatch at every call site.
const asIcon = (icon: unknown) => icon as ProviderIconComponent;

// Brand marks aren't available for every provider in the icon libraries
// already used by this app (@carbon/icons-react, lucide-react) or in the
// simple-icons set (no OpenAI/Microsoft/IBM entries as of react-icons 5.7.0),
// so this map draws from whichever bundled icon set has the right logo.
// Providers without an official mark available fall back to a generic icon
// below rather than blocking the feature on sourcing a custom SVG for each.
const PROVIDER_ICON: Partial<Record<Provider, ProviderIconComponent>> = {
  openai: asIcon(VscOpenai),
  claude: asIcon(SiAnthropic),
  gemini: asIcon(SiGooglegemini),
  'azure-openai': asIcon(VscAzure),
  'azure-openai-gateway': asIcon(VscAzure),
  ollama: asIcon(SiOllama),
};

// Fallback for any provider without a mapped brand icon above — covers
// ibm-watson-x today, and any future provider added to PROVIDERS before a
// matching logo is wired in here.
const FALLBACK_ICON: ProviderIconComponent = Sparkles;

export function ProviderIcon({
  provider,
  className,
}: {
  provider: Provider;
  className?: string;
}) {
  const Icon = PROVIDER_ICON[provider] ?? FALLBACK_ICON;
  return (
    <span aria-hidden="true">
      <Icon className={className} />
    </span>
  );
}
